import {
	deleteExchangeEmailByIds,
	EmailScreenModel,
	getEmailScreenModel
} from "client/Api/Email/email";
import {
	ContextActionBar,
	Dialog,
	renderReactDialog,
	Toolbar
} from "client/Components";
import {
	ensureNotNil,
	isNotNil
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import { ExchangeEmailId } from "client/Api/Email/common";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid } from "client_legacy/ndaw/guid";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { EmailComposer, EmailComposerHandle } from "../EmailContent/EmailComposer/EmailComposer";
import {
	CreateNewEmailSessionState,
	EditDraftEmailSessionState,
	EmailSessionStateKind
} from "../EmailPage/EmailPage";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useStableImperativeCallback,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { useSignatureStore } from "../Signatures/signatureStore";
import styles from "./EmailComposerDialog.module.less";
import { useSignatureSettingsDialog } from "../Signatures/SignatureSettingsDialog/SignatureSettingsDialog";
import {
	ComposeEmailAction,
	ComposeEmailActionContext,
	ComposeEmailActions
} from "../Actions/composeActions";
import { UploadAttachmentInProgressDialog } from "./UploadAttachmentInProgressDialog";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { EmailNotConfiguredDialog } from "./EmailNotConfiguredDialog";
import { EmailEvents, publish } from "client_legacy/Email/emailEvents";

export type NewEmailComposerDialogProps = {
	isNew: true;
	to?: string | nil;
	cc?: string | nil;
	bcc?: string | nil;
	smtpAddress?: string | nil;

	draftId?: undefined;
	doesRepresentNewlyGeneratedDraft?: undefined;
};

export type EditDraftComposerDialogProps = {
	isNew: false;
	draftId: string;
	doesRepresentNewlyGeneratedDraft: boolean;
	smtpAddress?: string | nil;

	to?: undefined;
	cc?: undefined;
	bcc?: undefined;
};

export type EmailComposerDialogProps = {
	onClose(): void;
} & (
	| NewEmailComposerDialogProps
	| EditDraftComposerDialogProps
);

export const EmailComposerDialog = (props: EmailComposerDialogProps): ReactElement => {
	const {
		isNew,
		to,
		cc,
		bcc,
		smtpAddress,
		draftId,
		doesRepresentNewlyGeneratedDraft = false,
		onClose
	} = props;

	const {
		signatures,
		setSignatures
	} = useSignatureStore(
		x => ({
			signatures: x.signatures,
			setSignatures: x.setSignatures
		}),
		shallowComparison
	);

	const [sessionState] = useState<CreateNewEmailSessionState | EditDraftEmailSessionState>(
		(): CreateNewEmailSessionState | EditDraftEmailSessionState => {
			if (isNew) {
				return {
					kind: EmailSessionStateKind.NewEmail,
					sessionId: newGuid(),
					to,
					cc,
					bcc
				};
			} else {
				return {
					kind: EmailSessionStateKind.EditDraft,
					emailId: { id: ensureNotNil(draftId) },
					sessionId: newGuid(),
					smtpAddress: smtpAddress ?? null,
					doesRepresentNewlyGeneratedDraft
				};
			}
		}
	);

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const [screenModel, setScreenModel] = useState<EmailScreenModel>();
	const stableOnClose: typeof onClose = useStableImperativeCallback(onClose);

	const alertManager: AlertManager = useAlerts();

	const [signatureSettingsDialog, openSignatureSettingsDialog] = useSignatureSettingsDialog();
	const [composerHandle, setComposerHandle] = useState<EmailComposerHandle | null>(null);
	const [hasUploadingAttachments, setHasUploadingAttachments] = useState<boolean>(false);
	const [shouldShowAttachmentInProgressDialog, setShouldShowAttachmentInProgressDialog] = useState<boolean>(false);

	const {
		isExchangeConfigured,
		hasIntegrationCredentialsForExchange
	} = useMemo(
		(): {
			isExchangeConfigured: boolean;
			hasIntegrationCredentialsForExchange: boolean;
		} => {
			const {
				exchange: {
					isConfigured: isExchangeConfigured
				},
				integrationCredentialsConfiguredLinks: {
					exchange: hasIntegrationCredentialsForExchange
				}
			} = userConfiguration();

			return {
				isExchangeConfigured,
				hasIntegrationCredentialsForExchange
			};
		},
		[]
	);

	const shouldShowExchangeIsNotConfiguredDialog: boolean = !isExchangeConfigured
		|| (
			isNotNil(screenModel)
				&& screenModel.useIntegrationCredentials
				&& !hasIntegrationCredentialsForExchange
		);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchScreenModel(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					const screenModel: EmailScreenModel = await getEmailScreenModel(signal);
					setScreenModel(screenModel);
					setSignatures(
						[
							screenModel.signatures.personal,
							screenModel.signatures.managed
						]
							.filter(isNotNil)
					);
					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					stableOnClose();
				}
			}

			if (!isExchangeConfigured) {
				return undefined;
			}

			const cancellation = new AbortController();
			fetchScreenModel(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			stableOnClose,
			setSignatures,
			isExchangeConfigured
		]
	);

	const sendHandler = useCallback(
		(): void => {
			ensureNotNil(composerHandle)
				.send();
		},
		[composerHandle]
	);

	const cancelHandler = useCallback(
		(): void => {
			composerHandle?.cancel();
		},
		[composerHandle]
	);

	const actions = useMemo(
		(): readonly ComposeEmailAction[] => {
			const composeActions = new ComposeEmailActions(
				{
					hasSendAction: false,
					hasDiscardAction: false,
					signatures: signatures,
					callbacks: {
						onIsInteractingWithBackendChange: setIsBusy,
						onOpenSignatureSettingsDialog: openSignatureSettingsDialog
					}
				},
				(): boolean => {
					if (hasUploadingAttachments) {
						setShouldShowAttachmentInProgressDialog(true);
						return false;
					}

					return true;
				}
			);

			return composeActions.getAllActions();
		},
		[
			openSignatureSettingsDialog,
			signatures,
			hasUploadingAttachments
		]
	);

	const actionContext = useMemo(
		(): ComposeEmailActionContext | null => {
			if (!composerHandle) {
				return null;
			}

			return {
				isMobile,
				composerHandle
			};
		},
		[composerHandle, isMobile]
	);

	const emailSentHandler = useCallback(
		(emailId: ExchangeEmailId): void => {
			onClose();
			publish(
				EmailEvents.emailsDeleted,
				{
					emails: [{ emailId, isRead: true }],
					isDraft: true
				}
			);
		},
		[onClose]
	);

	const deleteEmailHandler = useCallback(
		async (emailId: ExchangeEmailId): Promise<void> => {
			await deleteExchangeEmailByIds(
				{
					smtpAddress: isNotNil(smtpAddress)
						? { isSet: true, value: smtpAddress }
						: { isSet: false },
					emailIds: [emailId]
				},
				true
			);

			publish(
				EmailEvents.emailsDeleted,
				{
					emails: [{ emailId, isRead: true }],
					isDraft: true
				}
			);

			onClose();
		},
		[onClose, smtpAddress]
	);

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="email-composer-dialog"
			variant="left-slider"
			onClose={cancelHandler}
		>
			<Dialog.Header
				title={localize("_NewEmailFull")}
				onClose={cancelHandler}
				hasBorder
			/>
			<Dialog.Body goesEdgeToEdge>
				{isNotNil(screenModel) && (
					<>
						{actionContext && (
							<ContextActionBar
								hasBorderBottom
								actions={actions}
								context={actionContext}
								trackingId="action-bar"
							/>
						)}

						<div className={styles.composerWrapper}>
							<EmailComposer
								ref={setComposerHandle}
								defaultComposerFontStyle={screenModel.defaultComposerFontStyle}
								sessionState={sessionState}
								smtpAddress={smtpAddress ?? null}
								onEmailSent={emailSentHandler}
								onDeleteEmail={deleteEmailHandler}
								fromAddressOptions={screenModel.availableFroms}
								onHasUploadingAttachmentsChange={setHasUploadingAttachments}
								onDraftSaved={onClose}
								onIsBusyChange={setIsBusy}
							/>
						</div>
					</>
				)}
				{signatureSettingsDialog}
				{shouldShowAttachmentInProgressDialog && (
					<UploadAttachmentInProgressDialog
						onClose={() => setShouldShowAttachmentInProgressDialog(false)}
					/>
				)}
				{shouldShowExchangeIsNotConfiguredDialog && (
					<EmailNotConfiguredDialog
						shouldNavigateBackToWorkspaceOnClose={false}
						onClose={onClose}
					/>
				)}
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						isDisabled={!composerHandle}
						kind="primary"
						text={localize("_Send")}
						onClick={sendHandler}
						trackingId="send-button"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						responsiveBehavior="hide"
						onClick={cancelHandler}
						trackingId="cancel-button"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
EmailComposerDialog.displayName = "EmailComposerDialog";

export function openEmailComposerDialog(props: NewEmailComposerDialogProps | EditDraftComposerDialogProps): void {
	renderReactDialog(
		(onClose) => (
			<EmailComposerDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}